import produce from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, FragmentType, ValidationActionType } from 'models/enum';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  COLLECTION_CONFIGS,
  CollectionKey,
  EDCollectionConfig,
  EDRepeatableConfig,
  EDStepConfig,
  FIELD_CONFIGS,
  FIELD_CONFIG_MAP,
  FormField,
  REPEATABLE_CONFIGS,
  RepeatableKey,
  STEP_CONFIGS,
  SectionTitle
} from './constants';
import EmergencyDepartmentView, { EmergencyDepartmentViewProps } from './EmergencyDepartmentView';

// The Emergency Department chart maps every field to a single nav id; the 5 steps are a FE layout concern.
// 036 is Annual Ambulatory Care and 037 is Advance Directive in the facade config, so ED uses 038.
export const EMERGENCY_DEPARTMENT_NAV_ID = NAV_ID.EMERGENCY_DEPARTMENT;

export type EDRow = Record<string, string>;

export interface EDDynamicState {
  collections: Record<CollectionKey, EDRow[]>;
  repeatables: Record<RepeatableKey, EDRow[]>;
}

export interface EmergencyDepartmentProps extends ChartComponentProps {
  enableMultiStepsAuthoring?: (isSupportMultiStepsAuthoring: boolean) => void;
  navigateToSavedPatientCharting: VoidFunction;
}

const LAST_STEP_INDEX = STEP_CONFIGS.length - 1;
const COLLECTION_KEYS = Object.values(CollectionKey);
const REPEATABLE_KEYS = Object.values(RepeatableKey);

const buildFormField = (name: FormField): ChartMetaFormField => {
  const fieldConfig = FIELD_CONFIG_MAP.get(name);
  const formField = chartHelper.createFormField({ name, type: fieldConfig.type, label: fieldConfig.label });
  return { ...formField, value: '' };
};

export const buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
  const dataMap = new Map<string, ChartMetaFormField>();
  FIELD_CONFIGS.forEach((fieldConfig) => dataMap.set(fieldConfig.name, buildFormField(fieldConfig.name)));
  return dataMap;
};

const emptyRow = (config: EDCollectionConfig | EDRepeatableConfig): EDRow => {
  const items: { key: string }[] = 'columns' in config ? config.columns : config.fields;
  return items.reduce<EDRow>((row, col) => ({ ...row, [col.key]: '' }), {});
};

export const buildDefaultDynamicState = (): EDDynamicState => ({
  collections: COLLECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {} as Record<CollectionKey, EDRow[]>),
  repeatables: REPEATABLE_KEYS.reduce((acc, key) => ({ ...acc, [key]: [emptyRow(REPEATABLE_CONFIGS[key])] }), {} as Record<RepeatableKey, EDRow[]>)
});

const cloneFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> =>
  new Map(Array.from(formFieldMap.entries()).map(([name, field]) => [name, { ...field }]));

const clearField = (field: ChartMetaFormField): ChartMetaFormField => ({
  ...field,
  value: '',
  chartContent: field.chartContent?.map((item) => ({ ...item, selected: false }))
});

const resetStepState = (
  step: EDStepConfig,
  formFieldMap: Map<string, ChartMetaFormField>,
  initState: (map: Map<string, ChartMetaFormField>) => void,
  setDynamic: (updater: (current: EDDynamicState) => EDDynamicState) => void
): void => {
  if (step.sections) {
    const cleared = cloneFieldMap(formFieldMap);
    step.sections
      .flatMap((section) => section.rows.flat())
      .forEach((name) => {
        const field = cleared.get(name);
        if (field) cleared.set(name, clearField(field));
      });
    initState(cleared);
  }
  setDynamic((current) =>
    produce(current, (draft) => {
      (step.collections || []).forEach((key) => {
        draft.collections[key] = [];
      });
      (step.repeatables || []).forEach((key) => {
        draft.repeatables[key] = [emptyRow(REPEATABLE_CONFIGS[key])];
      });
    })
  );
};

const flattenChartRecords = (records: any[] = []): any[] =>
  records.flatMap((record) => {
    const childRecords = Array.isArray(record?.records) ? flattenChartRecords(record.records) : [];
    return record?.formField ? [record, ...childRecords] : childRecords;
  });

const buildHydratedField = (field: ChartMetaFormField, record: any): ChartMetaFormField => {
  const hydratedValue = record?.value ?? record?.content ?? '';
  const hydratedField: ChartMetaFormField = { ...field, value: hydratedValue };

  if (!Array.isArray(field.chartContent) || field.chartContent.length === 0) {
    return hydratedField;
  }

  const selectedContent = field.chartContent.find((item) => item.id === record?.contentId || item.value === hydratedValue || item.label === record?.content);
  if (!selectedContent) return hydratedField;

  return {
    ...hydratedField,
    value: selectedContent.value,
    chartContent: field.chartContent.map((item) => ({ ...item, selected: item.id === selectedContent.id }))
  };
};

const sortChartingFirst = (fragments: ChartFragment[]): ChartFragment[] =>
  [...fragments]
    .filter((fragment) => fragment?.chartData?.records?.length > 0)
    .sort((left, right) => Number(left.fragmentType !== FragmentType.CHARTING) - Number(right.fragmentType !== FragmentType.CHARTING));

export const buildAuthoredFormFields = (formFieldMap: Map<string, ChartMetaFormField>, fragments: ChartFragment[]): Map<string, ChartMetaFormField> => {
  const hydratedFieldMap = new Map(formFieldMap);

  sortChartingFirst(fragments).forEach((fragment) => {
    flattenChartRecords(fragment.chartData.records).forEach((record) => {
      if (!record?.formField || !FIELD_CONFIG_MAP.has(record.formField)) return;
      const currentField = hydratedFieldMap.get(record.formField);
      if (!currentField) return;
      hydratedFieldMap.set(record.formField, buildHydratedField(currentField, record));
    });
  });

  return hydratedFieldMap;
};

export const parseDynamicState = (fragments: ChartFragment[]): EDDynamicState => {
  const state = buildDefaultDynamicState();

  const parseRows = (section: any, columnKeys: string[]): EDRow[] =>
    (section?.tableRows || [])
      .map((cells: string[]) => {
        const row: EDRow = {};
        columnKeys.forEach((key, index) => {
          row[key] = String(cells?.[index] ?? '');
        });
        return row;
      })
      .filter((row: EDRow) => Object.values(row).some((value) => String(value).trim()));

  sortChartingFirst(fragments).forEach((fragment) => {
    (fragment.chartData?.records || []).forEach((section: any) => {
      const collectionKey = COLLECTION_KEYS.find((key) => COLLECTION_CONFIGS[key].title === section?.sectionTitle);
      if (collectionKey) {
        const rows = parseRows(
          section,
          COLLECTION_CONFIGS[collectionKey].columns.map((column) => column.key)
        );
        if (rows.length) state.collections[collectionKey] = rows;
        return;
      }
      const repeatableKey = REPEATABLE_KEYS.find((key) => REPEATABLE_CONFIGS[key].title === section?.sectionTitle);
      if (repeatableKey) {
        const rows = parseRows(
          section,
          REPEATABLE_CONFIGS[repeatableKey].fields.map((field) => field.key)
        );
        if (rows.length) state.repeatables[repeatableKey] = rows;
      }
    });
  });

  return state;
};

const buildEDRecord = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField) => {
  const { buildPlainRecord } = chartService;
  const formField = formFieldMap.get(fieldName);
  if (!formField) return null;

  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
  const title = fieldConfig?.label || formField.label || '';

  if (formField.inputType === FormFieldInputType.CHECK_BOX) {
    const isChecked = formField.value === true || String(formField.value ?? '').toLowerCase() === 'true' || Boolean(formField.chartContent?.some((item) => item.selected));
    if (!isChecked) return null;
    return buildPlainRecord({ formField: formField.name, title, content: true, value: 'true' });
  }

  const fieldValue = String(formField.value ?? '').trim();
  if (!fieldValue) return null;

  if (formField.inputType === FormFieldInputType.DROPDOWN) {
    const selectedContent = formField.chartContent?.find((item) => !!item.value && item.selected) || formField.chartContent?.find((item) => item.value === fieldValue);
    if (selectedContent) {
      return buildPlainRecord({ formField: formField.name, title, content: selectedContent.label, value: selectedContent.value, contentId: selectedContent.id });
    }
  }

  return buildPlainRecord({ formField: formField.name, title, content: formField.value, value: formField.value });
};

const buildDynamicSection = (prefix: string, groupTitle: string, title: string, columns: { key: string; label: string }[], rows: EDRow[]) => {
  const { buildPlainRecord } = chartService;
  const nonEmptyRows = rows.filter((row) => columns.some((column) => String(row[column.key] ?? '').trim()));
  return {
    sectionTitle: title,
    groupTitle,
    tableColumns: columns.map((column) => column.label),
    tableRows: nonEmptyRows.map((row) => columns.map((column) => String(row[column.key] ?? '').trim())),
    records: nonEmptyRows
      .flatMap((row, rowIndex) =>
        columns.map((column) => {
          const cell = String(row[column.key] ?? '').trim();
          if (!cell) return null;
          return buildPlainRecord({ formField: `${prefix}-${rowIndex}-${column.key}`, title: column.label, content: cell, value: cell });
        })
      )
      .filter(Boolean)
  };
};

export const buildFragment = (formFieldMap: Map<string, ChartMetaFormField>, dynamic: EDDynamicState, chartingTime: string): ChartFragment => {
  const sections = STEP_CONFIGS.flatMap((step) => {
    const parts: unknown[] = [];

    if (step.sections?.length) {
      parts.push({
        sectionTitle: step.title,
        groupTitle: step.title,
        records: step.sections.flatMap((section) => section.rows.flat()).map((fieldName) => buildEDRecord(formFieldMap, fieldName))
      });
    }

    (step.collections || []).forEach((key) => {
      const config = COLLECTION_CONFIGS[key];
      parts.push(buildDynamicSection(key, step.title, config.title, config.columns, dynamic.collections[key] || []));
    });

    (step.repeatables || []).forEach((key) => {
      const config = REPEATABLE_CONFIGS[key];
      parts.push(
        buildDynamicSection(
          key,
          step.title,
          config.title,
          config.fields.map((field) => ({ key: field.key, label: field.label })),
          dynamic.repeatables[key] || []
        )
      );
    });

    return parts;
  });

  const record = {
    chartTitle: Title.SPECIAL_CHARTS,
    fragmentTitle: SectionTitle.EMERGENCY_DEPARTMENT,
    records: sections
  };

  const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
  const basicInfo = chartService.createBaseFragment({ chartingTime });
  return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
};

const asOptionalFields = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => {
  const optionalMap = new Map<string, ChartMetaFormField>();
  formFieldMap.forEach((field, name) => {
    optionalMap.set(name, {
      ...field,
      isRequired: false,
      errors: (field.errors || []).filter((error) => error?.type !== ValidationActionType.REQUIRED)
    });
  });
  return optionalMap;
};

const useEmergencyDepartmentDynamicState = () => {
  const [dynamic, setDynamic] = useState<EDDynamicState>(buildDefaultDynamicState);

  const onReplaceCollection = useCallback((key: CollectionKey, rows: EDRow[]) => {
    setDynamic((current) =>
      produce(current, (draft) => {
        draft.collections[key] = rows;
      })
    );
  }, []);

  const onAddRepeatableRow = useCallback((key: RepeatableKey) => {
    setDynamic((current) =>
      produce(current, (draft) => {
        draft.repeatables[key].push(emptyRow(REPEATABLE_CONFIGS[key]));
      })
    );
  }, []);

  const onRepeatableChange = useCallback((key: RepeatableKey, index: number, fieldKey: string, value: string) => {
    setDynamic((current) =>
      produce(current, (draft) => {
        draft.repeatables[key][index][fieldKey] = value;
      })
    );
  }, []);

  const onDeleteRepeatableRow = useCallback((key: RepeatableKey, index: number) => {
    setDynamic((current) =>
      produce(current, (draft) => {
        draft.repeatables[key].splice(index, 1);
        if (draft.repeatables[key].length === 0) draft.repeatables[key].push(emptyRow(REPEATABLE_CONFIGS[key]));
      })
    );
  }, []);

  return { dynamic, setDynamic, handlers: { onReplaceCollection, onAddRepeatableRow, onRepeatableChange, onDeleteRepeatableRow } };
};

const useStepNavigation = (isAuthor: boolean, onSave: VoidFunction, resetFormSubmitted?: Function) => {
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);

  const onSelectStep = useCallback(
    (stepIndex: number) => {
      // Authors walk the steps in order; nothing is mandatory, so reaching a step is the only gate.
      if (isAuthor && stepIndex > furthestStep) return;
      setActiveStep(stepIndex);
      if (resetFormSubmitted) resetFormSubmitted();
    },
    [furthestStep, isAuthor, resetFormSubmitted]
  );

  const onBackStep = useCallback(() => {
    setActiveStep((current) => Math.max(0, current - 1));
    if (resetFormSubmitted) resetFormSubmitted();
  }, [resetFormSubmitted]);

  const onContinueStep = useCallback(() => {
    if (activeStep === LAST_STEP_INDEX) {
      if (isAuthor) onSave();
      return;
    }
    const nextStep = activeStep + 1;
    setFurthestStep((previous) => Math.max(previous, nextStep));
    setActiveStep(nextStep);
    if (resetFormSubmitted) resetFormSubmitted();
  }, [activeStep, isAuthor, onSave, resetFormSubmitted]);

  // Ticked once the user has moved past a step.
  const completedSteps = STEP_CONFIGS.map((_, index) => index < furthestStep);

  return { activeStep, maxSelectableStep: furthestStep, completedSteps, onSelectStep, onBackStep, onContinueStep };
};

const EmergencyDepartment = (props: EmergencyDepartmentProps) => {
  const {
    chartingTime,
    displayAuthoringData,
    enableDisplayRecordsButton,
    enableMultiStepsAuthoring,
    formFieldMap,
    formSubmittedCount,
    initState,
    navigateToSavedPatientCharting,
    resetFormSubmitted,
    saveChart
  } = props;
  const isAuthor = Boolean(props.isAuthor);
  const { dynamic, setDynamic, handlers } = useEmergencyDepartmentDynamicState();
  const safeFormFieldMap = formFieldMap || buildDefaultFormFields();
  const optionalFormFieldMap = useMemo(() => asOptionalFields(safeFormFieldMap), [safeFormFieldMap]);

  useEffect(() => {
    initState(buildDefaultFormFields());
    if (enableMultiStepsAuthoring) enableMultiStepsAuthoring(true);
  }, [enableMultiStepsAuthoring, initState]);

  useEffect(() => {
    if (!props.loadChartData || props.selectedNavId !== EMERGENCY_DEPARTMENT_NAV_ID) return;

    props
      .loadChartData([FragmentType.CHARTING, FragmentType.AUTHORED], [props.selectedNavId])
      .then(({ data }) => {
        if (!data?.length) return;
        initState(buildAuthoredFormFields(buildDefaultFormFields(), data));
        setDynamic(parseDynamicState(data));
      })
      .catch(() => ({ data: [] }));
  }, [initState, props.loadChartData, props.selectedNavId]);

  const handleSaveClick = useCallback(() => {
    saveChart([buildFragment(safeFormFieldMap, dynamic, chartingTime)], {
      formFieldMap: optionalFormFieldMap,
      afterSave: () => {
        if (!isAuthor) navigateToSavedPatientCharting();
      }
    });
  }, [chartingTime, dynamic, isAuthor, navigateToSavedPatientCharting, optionalFormFieldMap, safeFormFieldMap, saveChart]);

  const { activeStep, maxSelectableStep, completedSteps, onSelectStep, onBackStep, onContinueStep } = useStepNavigation(isAuthor, handleSaveClick, resetFormSubmitted);

  const handleResetStep = useCallback(
    () => resetStepState(STEP_CONFIGS[activeStep], safeFormFieldMap, initState, setDynamic),
    [activeStep, initState, safeFormFieldMap, setDynamic]
  );

  const viewProps: EmergencyDepartmentViewProps = {
    activeStep,
    collections: dynamic.collections,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton: Boolean(enableDisplayRecordsButton),
    formFieldMap: optionalFormFieldMap,
    formSubmittedCount,
    maxSelectableStep,
    isAuthor,
    onReplaceCollection: handlers.onReplaceCollection,
    onAddRepeatableRow: handlers.onAddRepeatableRow,
    onBackStep,
    onContinueStep,
    onDeleteRepeatableRow: handlers.onDeleteRepeatableRow,
    onRepeatableChange: handlers.onRepeatableChange,
    onResetStep: handleResetStep,
    onSelectStep,
    repeatables: dynamic.repeatables
  };

  return <EmergencyDepartmentView {...viewProps} />;
};

export { EmergencyDepartment as BaseEmergencyDepartment };
export default compose(withSavedPatientChartsPage, withChartLogic)(EmergencyDepartment);
